import React, { useState, useEffect, useCallback } from 'react';
import apiClient from '../utils/apiClient';
import eventEmitter from '../utils/eventEmitter';
import { useAuth } from '../contexts/AuthContext';
import { getLocalDateString, getPayPeriodInfo, countAvailableDaysInStick, getTodayString, addDays } from '../utils/dateUtils';

const ScheduleRequest = () => {
  const { user } = useAuth();
  const [scheduleData, setScheduleData] = useState([]);
  const [dailyCounts, setDailyCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [settings, setSettings] = useState({ mandatoryBreaks: false });
  const [userPermissions, setUserPermissions] = useState([]);
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);
  const [allScheduleData, setAllScheduleData] = useState([]); // New state for all schedule data

  const fetchSettings = useCallback(async () => {
    try {
      const response = await apiClient.get('/api/settings');
      setSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      // Default to false if we can't fetch settings
      setSettings({ mandatoryBreaks: false });
    }
  }, []);

  const fetchUserPermissions = useCallback(async () => {
    try {
      console.log('🔍 ScheduleRequest: Fetching user permissions...');
      const response = await apiClient.get('/api/user/permissions');
      console.log('🔍 ScheduleRequest: Received permissions:', response.data);
      setUserPermissions(response.data);
      setPermissionsLoaded(true);
    } catch (error) {
      console.error('Failed to fetch user permissions:', error);
      setPermissionsLoaded(true);
    }
  }, []);

  const fetchDailyCounts = useCallback(async (dates) => {
    try {
      const countPromises = dates.map(date => 
        apiClient.get(`/api/daily-count/${date}`).catch(() => ({ data: { lifeguardRookieCount: 0, isAtLimit: false } }))
      );
      const countResponses = await Promise.all(countPromises);
      
      const counts = {};
      countResponses.forEach((response, index) => {
        counts[dates[index]] = response.data;
      });
      
      setDailyCounts(counts);
    } catch (error) {
      console.error('Failed to fetch daily counts:', error);
    }
  }, []);

  // Env-driven client config
  const OFF_SEASON_START_MONTH = parseInt(process.env.REACT_APP_OFF_SEASON_START_MONTH || '9', 10); // September => 9
  const OFF_SEASON_START_DAY = parseInt(process.env.REACT_APP_OFF_SEASON_START_DAY || '1', 10);
  const SEASON_START_MONTH = parseInt(process.env.REACT_APP_SEASON_START_MONTH || '6', 10); // June => 6
  const SEASON_START_DAY = parseInt(process.env.REACT_APP_SEASON_START_DAY || '17', 10);
  const LOCKED_DAYS = parseInt(process.env.REACT_APP_LOCKED_DAYS || '3', 10); // today,tomorrow,day-after
  const DAILY_LIMIT = parseInt(process.env.REACT_APP_DAILY_LIMIT || '35', 10);
  const STICK_MAX_AVAILABLE_DAYS = parseInt(process.env.REACT_APP_STICK_MAX_AVAILABLE_DAYS || '5', 10);
  const PAYDAY_INTERVAL_DAYS = parseInt(process.env.REACT_APP_PAYDAY_INTERVAL_DAYS || '14', 10);
  const PAYDAY_REFERENCE_MONTH = parseInt(process.env.REACT_APP_PAYDAY_REFERENCE_MONTH || '7', 10); // July => 7
  const PAYDAY_REFERENCE_DAY = parseInt(process.env.REACT_APP_PAYDAY_REFERENCE_DAY || '4', 10);

  const fetchScheduleRequests = useCallback(async () => {
    try {
      const response = await apiClient.get('/api/schedule-requests');
      const requests = response.data;
      
      // Calculate dates from today until September 2nd
      const schedule = [];
      const dates = [];
      const today = new Date();
      
      // Set off-season start target date for the current year (default Sept 1)
      // If we're already past off-season start, use next year's
      const currentYear = today.getFullYear();
      let offSeasonStart = new Date(currentYear, OFF_SEASON_START_MONTH - 1, OFF_SEASON_START_DAY);
      
      // If off-season has already started this year, use next year
      if (today > offSeasonStart) {
        offSeasonStart = new Date(currentYear + 1, OFF_SEASON_START_MONTH - 1, OFF_SEASON_START_DAY);
      }
      
      // Calculate number of days from today until off-season start
      const timeDiff = offSeasonStart.getTime() - today.getTime();
      const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24)) + 1;
      
      for (let i = 0; i < daysDiff; i++) {
        const dateString = addDays(today, i);
        const [year, month, day] = dateString.split('-').map(Number);
        const date = new Date(year, month - 1, day);
        dates.push(dateString);
        const payPeriodInfo = getPayPeriodInfo(date);
        
        // More robust date matching - handle both ISO string and MySQL date formats
        const existingRequest = requests.find(r => {
          let requestDateString;
          if (r.request_date instanceof Date) {
            requestDateString = getLocalDateString(r.request_date);
          } else if (typeof r.request_date === 'string') {
            // Handle MySQL date strings properly - parse as local date to avoid timezone issues
            if (r.request_date.includes('T') || r.request_date.includes('Z')) {
              // If it's an ISO string, extract just the date part to avoid timezone conversion
              requestDateString = r.request_date.split('T')[0];
            } else {
              // If it's already in YYYY-MM-DD format, use it directly
              requestDateString = r.request_date;
            }
          } else {
            // Fallback - convert to Date and extract date string
            requestDateString = getLocalDateString(new Date(r.request_date));
          }
          
          return requestDateString === dateString;
        });
        
        // Block first N days (e.g., today, tomorrow, day after tomorrow)
        // Also block dates between off-season start and next season start
        let isDisabled = i <= (LOCKED_DAYS - 1);
        
        // Check if date falls between off-season start and next season start
        const currentDate = new Date(year, month - 1, day);
        const currentYearOffSeasonStart = new Date(currentDate.getFullYear(), OFF_SEASON_START_MONTH - 1, OFF_SEASON_START_DAY);
        const nextYearSeasonStart = new Date(currentDate.getFullYear() + 1, SEASON_START_MONTH - 1, SEASON_START_DAY);
        
        // If date is after off-season start and before next season start, disable it
        if (currentDate > currentYearOffSeasonStart && currentDate < nextYearSeasonStart) {
          isDisabled = true;
        }
        
        // Convert database boolean values (0/1) to JavaScript booleans (true/false)
        let availabilityValue = null;
        if (existingRequest) {
          if (existingRequest.is_available === 1 || existingRequest.is_available === true) {
            availabilityValue = true;
          } else if (existingRequest.is_available === 0 || existingRequest.is_available === false) {
            availabilityValue = false;
          }
        }
        
        schedule.push({
          date: dateString,
          displayDate: date.toLocaleDateString('en-US', { 
            weekday: 'short', 
            month: 'short', 
            day: 'numeric' 
          }),
          fullDate: date.toLocaleDateString('en-US', { 
            weekday: 'long', 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
          }),
          is_available: availabilityValue,
          isDisabled,
          dayIndex: i,
          payPeriodInfo
        });
      }
      
      // Store all requests for accurate stick counting (including past days)
      const allRequestsForStickCounting = requests.map(r => {
        let requestDateString;
        if (r.request_date instanceof Date) {
          requestDateString = getLocalDateString(r.request_date);
        } else if (typeof r.request_date === 'string') {
          if (r.request_date.includes('T') || r.request_date.includes('Z')) {
            requestDateString = r.request_date.split('T')[0];
          } else {
            requestDateString = r.request_date;
          }
        } else {
          requestDateString = getLocalDateString(new Date(r.request_date));
        }
        
        return {
          date: requestDateString,
          is_available: r.is_available === 1 || r.is_available === true
        };
      });
      
      setScheduleData(schedule);
      // Store complete data for stick counting
      setAllScheduleData(allRequestsForStickCounting);
      
      // Fetch daily counts for all dates
      await fetchDailyCounts(dates);
    } catch (error) {
      console.error('Failed to fetch schedule requests:', error);
      showMessage('Failed to load schedule data', 'error');
    } finally {
      setLoading(false);
    }
  }, [fetchDailyCounts]);

  useEffect(() => {
    fetchSettings();
    fetchScheduleRequests();
    if (user) {
      fetchUserPermissions();
    }
  }, [fetchSettings, fetchScheduleRequests, fetchUserPermissions, user]);

  const showMessage = (text, type) => {
    setMessage(text);
    setMessageType(type);
    setTimeout(() => {
      setMessage('');
      setMessageType('');
    }, 5000);
  };

  const canAddDay = (dateString) => {
    // Use all schedule data (including past days) for accurate stick counting
    const currentAvailableDaysInStick = countAvailableDaysInStick(dateString, allScheduleData);
    const stickNotFull = currentAvailableDaysInStick < STICK_MAX_AVAILABLE_DAYS;
    
    // Check daily limit for lifeguards and rookies (lieutenants+ can bypass)
    if (!canBypassDailyLimit() && isDailyLimitReached(dateString)) {
      return false;
    }
    
    // Check if adding this day would exceed 5 consecutive days
    if (wouldExceedConsecutiveDayLimit(dateString)) {
      return false;
    }
    
    return stickNotFull;
  };

  const isStickFull = (dateString) => {
    // Use all schedule data (including past days) for accurate stick counting
    const currentAvailableDaysInStick = countAvailableDaysInStick(dateString, allScheduleData);
    return currentAvailableDaysInStick >= STICK_MAX_AVAILABLE_DAYS;
  };

  const canBypassDailyLimit = () => {
    if (!permissionsLoaded) {
      // Conservative: return false during loading to prevent showing unauthorized functionality
      return false;
    }
    return userPermissions.includes('bypass_daily_limit');
  };

  const isDailyLimitReached = (dateString) => {
    const dailyData = dailyCounts[dateString];
    return dailyData && dailyData.isAtLimit;
  };

  // Check if adding this day would create more than 5 consecutive days
  const wouldExceedConsecutiveDayLimit = (dateString) => {
    // If mandatory breaks are disabled, return false
    if (!settings.mandatoryBreaks) {
      return false;
    }
    
    // Simulate adding this day to the complete schedule data and count total consecutive days
    const simulatedSchedule = [...allScheduleData];
    const existingIndex = simulatedSchedule.findIndex(day => day.date === dateString);
    if (existingIndex >= 0) {
      simulatedSchedule[existingIndex] = { ...simulatedSchedule[existingIndex], is_available: true };
    } else {
      simulatedSchedule.push({ date: dateString, is_available: true });
    }
    
    return getConsecutiveDaysCount(dateString, simulatedSchedule) > 5;
  };

  // Helper function to count consecutive days including the target date
  const getConsecutiveDaysCount = (dateString, schedule = allScheduleData) => {
    const targetDate = new Date(dateString);
    let consecutiveDays = 0;
    
    // Find the start of the consecutive sequence by going backwards
    let startDate = new Date(targetDate);
    while (true) {
      const checkDateString = getLocalDateString(startDate);
      const dayData = schedule.find(d => d.date === checkDateString);
      
      if (dayData && dayData.is_available === true) {
        startDate.setDate(startDate.getDate() - 1);
      } else {
        // Found the start of the sequence (one day after the last unavailable day)
        startDate.setDate(startDate.getDate() + 1);
        break;
      }
    }
    
    // Count consecutive days from the start
    let countDate = new Date(startDate);
    while (true) {
      const checkDateString = getLocalDateString(countDate);
      const dayData = schedule.find(d => d.date === checkDateString);
      
      if (dayData && dayData.is_available === true) {
        consecutiveDays++;
        countDate.setDate(countDate.getDate() + 1);
      } else {
        break;
      }
    }
    
    return consecutiveDays;
  };

  // Check if this day should show "Mandatory Break" (would be 6th+ consecutive day)
  const isMandatoryBreakDay = (dateString) => {
    // If mandatory breaks are disabled, return false
    if (!settings.mandatoryBreaks) {
      return false;
    }
    
    // Don't show mandatory break if this day is already available
    // Check both visible schedule and complete schedule data
    const visibleDayData = scheduleData.find(d => d.date === dateString);
    const allDayData = allScheduleData.find(d => d.date === dateString);
    if ((visibleDayData && visibleDayData.is_available === true) || 
        (allDayData && allDayData.is_available === true)) {
      return false;
    }
    
    return wouldExceedConsecutiveDayLimit(dateString);
  };

  const toggleAvailability = async (dateString, currentAvailability) => {
    const dayData = scheduleData.find(d => d.date === dateString);
    if (dayData?.isDisabled) {
      if (dayData.dayIndex <= (LOCKED_DAYS - 1)) {
        showMessage('Cannot edit availability for locked days at the start of the window', 'error');
      } else {
        // Check if this is disabled due to off-season
        const currentDate = new Date(dayData.date);
        const currentYearOffSeasonStart = new Date(currentDate.getFullYear(), OFF_SEASON_START_MONTH - 1, OFF_SEASON_START_DAY);
        const nextYearSeasonStart = new Date(currentDate.getFullYear() + 1, SEASON_START_MONTH - 1, SEASON_START_DAY);
        
        if (currentDate > currentYearOffSeasonStart && currentDate < nextYearSeasonStart) {
          showMessage('Cannot edit availability during off-season', 'error');
        } else {
          showMessage('This day is not editable', 'error');
        }
      }
      return;
    }

    // Check if trying to add a day (null -> true or false -> true)
    const newAvailability = currentAvailability === null ? true : !currentAvailability;
    
    if (newAvailability && !canAddDay(dateString)) {
      if (wouldExceedConsecutiveDayLimit(dateString)) {
        showMessage('Unable to add: Maximum 5 consecutive days allowed. Mandatory break required.', 'error');
      } else if (!canBypassDailyLimit() && isDailyLimitReached(dateString)) {
        const dailyData = dailyCounts[dateString];
        showMessage(`Unable to add: Daily limit reached (${dailyData?.lifeguardRookieCount || DAILY_LIMIT}/${DAILY_LIMIT} lifeguards and rookies). Users with "Bypass Daily Limit" permission can override this limit.`, 'error');
      } else {
        showMessage(`Unable to add: Maximum ${STICK_MAX_AVAILABLE_DAYS} days per stick allowed.`, 'error');
      }
      return;
    }

    setSaving(true);
    
    try {


      const response = await apiClient.post('/api/schedule-requests', {
        request_date: dateString,
        is_available: newAvailability
      });



      // Only update local state and emit event if server request was successful
      if (response.status === 200) {
        // Update local state
        setScheduleData(prev => prev.map(day => 
          day.date === dateString 
            ? { ...day, is_available: newAvailability }
            : day
        ));

        // Also update the complete schedule data for accurate stick counting
        setAllScheduleData(prevData => {
          const existingIndex = prevData.findIndex(day => day.date === dateString);
          if (existingIndex >= 0) {
            return prevData.map((day, index) => 
              index === existingIndex 
                ? { ...day, is_available: newAvailability }
                : day
            );
          } else {
            return [...prevData, { date: dateString, is_available: newAvailability }];
          }
        });

        // Refresh daily counts to reflect the change
        const allDates = scheduleData.map(day => day.date);
        await fetchDailyCounts(allDates);

        // Emit event to notify other components that schedule data has changed
        console.log('ScheduleRequest: About to emit scheduleUpdated event');
        eventEmitter.emit('scheduleUpdated', {
          date: dateString,
          is_available: newAvailability,
          timestamp: new Date(),
          user: 'current_user',
          action: 'toggle_availability',
          serverData: response.data.data // Include server verification data
        });
        console.log('ScheduleRequest: scheduleUpdated event emitted successfully');

        showMessage('Availability updated successfully', 'success');
      } else {
        throw new Error('Server returned non-200 status');
      }
    } catch (error) {
      console.error('Failed to update availability:', error);
      showMessage(
        error.response?.data?.error || 'Failed to update availability', 
        'error'
      );
    } finally {
      setSaving(false);
    }
  };

  const getScheduleDayClass = (day) => {
    let className = 'schedule-day';
    
    if (day.isDisabled) {
      className += ' disabled';
    } else if (isMandatoryBreakDay(day.date) && day.is_available !== true) {
      // Mandatory break day - show in yellow
      className += ' mandatory-break';
    } else if ((isStickFull(day.date) || (!canBypassDailyLimit() && isDailyLimitReached(day.date))) && day.is_available !== true) {
              // If stick is full OR daily limit reached (and not lieutenant+) and this day is not already available, show at-limit styling
      className += ' at-limit';
    } else if (day.is_available === true) {
      className += ' available';
    } else if (day.is_available === false) {
      className += ' unavailable';
    } else {
      // Check if at limit for adding new days
      if (!canAddDay(day.date)) {
        if (wouldExceedConsecutiveDayLimit(day.date)) {
          className += ' mandatory-break';
        } else {
          className += ' at-limit';
        }
      }
    }
    
    return className;
  };

  const getStatusText = (day) => {
    let status;
    if (day.isDisabled) {
      // For locked days, show availability status with color coding
      let lockText;
      if (day.dayIndex === 0) lockText = 'Today';
      else if (day.dayIndex === 1) lockText = 'Tomorrow';
      else if (day.dayIndex === 2) lockText = 'Day After';
      else if (day.dayIndex < LOCKED_DAYS) lockText = 'Locked';
      else {
        // Check if this is disabled due to off-season (between configured off-season start and season start)
        const currentDate = new Date(day.date);
        const currentYearOffSeasonStart = new Date(currentDate.getFullYear(), OFF_SEASON_START_MONTH - 1, OFF_SEASON_START_DAY);
        const nextYearSeasonStart = new Date(currentDate.getFullYear() + 1, SEASON_START_MONTH - 1, SEASON_START_DAY);
        
        if (currentDate > currentYearOffSeasonStart && currentDate < nextYearSeasonStart) {
          lockText = 'Off-Season';
        } else {
          lockText = 'Locked';
        }
      }
      
      // Add availability status for locked days
      if (day.is_available === true) {
        status = `${lockText} - Available`;
      } else if (day.is_available === false) {
        status = `${lockText} - Not Available`;
      } else {
        status = `${lockText} - Not Set`;
      }
    } else if (isMandatoryBreakDay(day.date) && day.is_available !== true) {
      // Mandatory break day after 5 consecutive days
      status = 'Mandatory Break';
    } else if ((!canBypassDailyLimit() && isDailyLimitReached(day.date)) && day.is_available !== true) {
      // If daily limit reached and user can't bypass, show limit message
      const dailyData = dailyCounts[day.date];
      status = `Daily limit reached (${dailyData?.lifeguardRookieCount || DAILY_LIMIT}/${DAILY_LIMIT})`;
    } else if (isStickFull(day.date) && day.is_available !== true) {
      // If stick is full and this day is not already available, show full message
      const payPeriodInfo = day.payPeriodInfo;
      status = `Stick ${payPeriodInfo?.stickNumber || 'current'} is full (${STICK_MAX_AVAILABLE_DAYS}/${STICK_MAX_AVAILABLE_DAYS} days)`;
    } else if (day.is_available === true) {
      status = 'Available';
    } else if (day.is_available === false) {
      status = 'Not Available';
    } else {
      // Check if at limit for adding new days
      if (!canAddDay(day.date)) {
        if (wouldExceedConsecutiveDayLimit(day.date)) {
          status = 'Mandatory Break';
        } else if (!canBypassDailyLimit() && isDailyLimitReached(day.date)) {
          const dailyData = dailyCounts[day.date];
          status = `Daily limit reached (${dailyData?.lifeguardRookieCount || DAILY_LIMIT}/${DAILY_LIMIT})`;
        } else {
          const payPeriodInfo = day.payPeriodInfo;
          status = `Stick ${payPeriodInfo?.stickNumber || 'current'} is full (${STICK_MAX_AVAILABLE_DAYS}/${STICK_MAX_AVAILABLE_DAYS} days)`;
        }
      } else {
        status = 'Click to Set';
      }
    }
    
    return status;
  };

  const getStatusClass = (day) => {
    if (day.isDisabled) {
      // For locked days, apply color coding based on availability status
      if (day.is_available === true) {
        return 'status-locked-available';
      } else if (day.is_available === false) {
        return 'status-locked-unavailable';
      } else {
        return 'status-locked-not-set';
      }
    } else if (isMandatoryBreakDay(day.date) && day.is_available !== true) {
      // Mandatory break day - yellow styling
      return 'status-mandatory-break';
    } else if ((isStickFull(day.date) || (!canBypassDailyLimit() && isDailyLimitReached(day.date))) && day.is_available !== true) {
              // If stick is full OR daily limit reached (and not lieutenant+) and this day is not already available, use at-limit style
      return 'status-at-limit';
    } else if (day.is_available === true) {
      return 'status-available';
    } else if (day.is_available === false) {
      return 'status-unavailable';
    } else {
      // Check if at limit for adding new days
      if (!canAddDay(day.date)) {
        if (wouldExceedConsecutiveDayLimit(day.date)) {
          return 'status-mandatory-break';
        } else {
          return 'status-at-limit';
        }
      } else {
        return 'status-not-set';
      }
    }
  };

  // Function to determine if a date is a payday
  const isPayday = (dateString) => {
    // Reference: Second payday was July 4th, paydays occur every 14 days
    const checkDate = new Date(dateString + 'T00:00:00'); // Parse as local time to avoid timezone issues
    const currentYear = checkDate.getFullYear();
    const referencePayday = new Date(currentYear, 6, 4, 0, 0, 0); // July 4 of the same year at midnight
    
    // Normalize both dates to avoid timezone issues
    checkDate.setHours(0, 0, 0, 0);
    referencePayday.setHours(0, 0, 0, 0);
    
    // Calculate days difference
    const timeDiff = checkDate.getTime() - referencePayday.getTime();
    const daysDiff = Math.round(timeDiff / (1000 * 60 * 60 * 24));
    
    // Check if the date falls on a payday (every 14 days from reference)
    return daysDiff % 14 === 0;
  };

  const getPayPeriodDisplayInfo = (day) => {
    const payPeriodInfo = day.payPeriodInfo;
    // Use all schedule data (including past days) for accurate stick counting
    const currentAvailableDaysInStick = countAvailableDaysInStick(day.date, allScheduleData);
    
    // Show pay period info for all editable days
    if (!payPeriodInfo || day.isDisabled) {
      return null;
    }
    
    return {
      payPeriod: payPeriodInfo.payPeriodNumber,
      stick: payPeriodInfo.stickNumber,
      stickDays: currentAvailableDaysInStick,
      guards: 0 // TODO: Implement guard count when needed
    };
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="loading-container" style={{ height: '400px' }}>
          <div className="loading-spinner"></div>
          <p>Loading schedule...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <h1 className="page-title">Schedule Request</h1>
        <p className="page-subtitle">Mark your availability through the in-season window</p>
      </div>

      {message && (
        <div className={`alert alert-${messageType}`}>
          {message}
        </div>
      )}

      <div className="card">
        <div className="card-header">
          <h2 className="card-title">Your Availability</h2>
        </div>

        <div className="alert alert-info">
          <strong>Instructions:</strong>
          <ul style={{ marginBottom: 0, paddingLeft: '1.5rem' }}>
            <li>Click on a day to toggle your availability</li>
            <li>Green = Available, Red = Not Available, Gray = Not Set, Yellow = Limit Reached{settings.mandatoryBreaks ? '/Mandatory Break' : ''}</li>
            <li>💵 = Payday (occurs every {PAYDAY_INTERVAL_DAYS} days)</li>
            <li>You cannot edit the first {LOCKED_DAYS} day(s) (locked)</li>
            <li><strong>Locked days show color-coded availability status:</strong> Green text = Available, Red text = Not Available</li>
            <li>Changes allowed after locked days up to off-season start</li>
            {settings.mandatoryBreaks && (
              <li><strong>Maximum 5 consecutive days allowed - mandatory break required after 5 days in a row</strong></li>
            )}
            <li>Remember: You can work maximum {STICK_MAX_AVAILABLE_DAYS} days per stick</li>
            <li>Daily limit: Maximum {DAILY_LIMIT} lifeguards and rookies per day (users with "Bypass Daily Limit" permission can override)</li>
            <li>When limits are reached, remaining days turn yellow</li>
            {canBypassDailyLimit() && (
              <li><strong>You have permission to bypass the daily limit of {DAILY_LIMIT}</strong></li>
            )}
          </ul>
        </div>

        <div className="schedule-grid">
          {scheduleData.map((day) => {
            const payPeriodDisplay = getPayPeriodDisplayInfo(day);
            const isClickable = !saving && !day.isDisabled && (
              day.is_available !== null || 
              canAddDay(day.date)
            );
            
            return (
              <div
                key={day.date}
                className={getScheduleDayClass(day)}
                onClick={() => isClickable && toggleAvailability(day.date, day.is_available)}
                style={{ 
                  cursor: isClickable ? 'pointer' : 'not-allowed',
                  opacity: saving ? 0.7 : 1,
                  position: 'relative'
                }}
              >
                <div className="schedule-date">
                  {day.displayDate}
                </div>
                {isPayday(day.date) && (
                  <span 
                    style={{ 
                      position: 'absolute',
                      bottom: '4px',
                      right: '4px',
                      color: '#10b981', 
                      fontSize: '12px',
                      fontWeight: 'bold',
                      lineHeight: '1',
                      zIndex: 1
                    }}
                    title="Payday"
                  >
                    💵
                  </span>
                )}
                <div className={`schedule-status ${getStatusClass(day)}`}>
                  {getStatusText(day)}
                </div>
                {payPeriodDisplay && (
                  <div className="pay-period-info" style={{ marginTop: '0.5rem', fontSize: '12px', color: '#4a5568', lineHeight: '1.2' }}>
                    <div>Pay Period {payPeriodDisplay.payPeriod} - Stick {payPeriodDisplay.stick}</div>
                    <div>Stick Days {payPeriodDisplay.stickDays}/{STICK_MAX_AVAILABLE_DAYS}</div>
                    {dailyCounts[day.date] && (
                      <div style={{ color: dailyCounts[day.date].isAtLimit ? '#d97706' : '#4a5568' }}>
                        Guards: {dailyCounts[day.date].lifeguardRookieCount}/{DAILY_LIMIT}
                      </div>
                    )}
                  </div>
                )}
                {day.isDisabled && (
                  <div className="text-sm" style={{ marginTop: '0.5rem', color: '#718096' }}>
                    {day.dayIndex === 0 ? 'Cannot edit' : 
                     (day.dayIndex < LOCKED_DAYS) ? 'Locked' :
                     (() => {
                       const currentDate = new Date(day.date);
                       const currentYearOffSeasonStart = new Date(currentDate.getFullYear(), OFF_SEASON_START_MONTH - 1, OFF_SEASON_START_DAY);
                       const nextYearSeasonStart = new Date(currentDate.getFullYear() + 1, SEASON_START_MONTH - 1, SEASON_START_DAY);
                       return (currentDate > currentYearOffSeasonStart && currentDate < nextYearSeasonStart) ? 'Off-Season' : 'Locked';
                     })()}
                  </div>
                )}
                {day.dayIndex > (LOCKED_DAYS - 1) && !day.isDisabled && (
                  <div className="text-sm" style={{ marginTop: '0.25rem', color: '#4a5568' }}>
                    {payPeriodDisplay ? 
                      `Day ${day.payPeriodInfo.dayInPayPeriod} of Pay Period` : 
                      'Editable'
                    }
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h2 className="card-title">Schedule Summary</h2>
        </div>
        
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Available Days</label>
            <p className="mb-0 text-lg font-bold" style={{ color: 'var(--success-color)' }}>
              {scheduleData.filter(d => d.is_available === true).length} days
            </p>
          </div>
          <div className="form-group">
            <label className="form-label">Unavailable Days</label>
            <p className="mb-0 text-lg font-bold" style={{ color: 'var(--danger-color)' }}>
              {scheduleData.filter(d => d.is_available === false).length} days
            </p>
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Not Set (Editable Days)</label>
          <p className="mb-0 text-lg font-bold" style={{ color: 'var(--gray-600)' }}>
            {scheduleData.filter(d => d.is_available === null && !d.isDisabled).length} days
          </p>
        </div>
      </div>
    </div>
  );
};

export default ScheduleRequest; 